<template>
  <div v-if="parameter.filter" :class="[ 'w-full pt-4 border-t border-primary-neutral', isOpen ? 'mb-4' : 'mb-2' ]">
    <button
      :class="[ 'flex items-center w-full text-start outline-none focus:outline-none focus:ring-0 hover:no-underline text-base font-bold pb-2', isDetail ? 'text-bolder' : 'text-heading' ]"
    >
      <div class="flex justify-between items-center w-full cursor-pointer" @click="toggleCollapse">
        <label :htmlFor="`parameter_${parameter.idParameter}`">
          {{ parameter.name }}
        </label>
        <div :class="['ms-auto', isDetail ? 'text-bolder' : 'text-heading']">
          <MinusIcon v-if="isOpen" class="w-6 h-6" />
          <PlusIcon v-else class="w-6 h-6" />
        </div>
      </div>
    </button>
    <template v-if="isOpen">
      <div v-if="parameter.filter === 'slider'" class="pt-2" role="group" :aria-label="parameter.name">
        <div class="flex justify-between text-sm text-heading">
          <label :for="`${groupName}_min`">Min: {{ range.valueMin }}</label>
          <label :for="`${groupName}_max`">Max: {{ range.valueMax }}</label>
        </div>
        <div class="parameter-range relative h-8 mx-3 text-accent" dir="ltr">
          <div class="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-primary-neutral" />
          <div
            class="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-accent"
            :style="rangeTrackStyle"
          />
          <input
            :id="`${groupName}_min`"
            v-model.number="range.valueMin"
            type="range"
            :min="rangeLimits.min"
            :max="rangeLimits.max"
            :aria-label="`${parameter.name}: Min`"
            :aria-valuemax="range.valueMax"
            step="0.1"
            class="parameter-range-input"
            :style="{ zIndex: range.valueMin > (rangeLimits.min + rangeLimits.max) / 2 ? 3 : 2 }"
            @input="clampRange('valueMin')"
            @change="handleRangeChanged"
          >
          <input
            :id="`${groupName}_max`"
            v-model.number="range.valueMax"
            type="range"
            :min="rangeLimits.min"
            :max="rangeLimits.max"
            :aria-label="`${parameter.name}: Max`"
            :aria-valuemin="range.valueMin"
            step="0.1"
            class="parameter-range-input"
            :style="{ zIndex: range.valueMin > (rangeLimits.min + rangeLimits.max) / 2 ? 2 : 3 }"
            @input="clampRange('valueMax')"
            @change="handleRangeChanged"
          >
        </div>
      </div>
      <FilterParameterValue
        v-if="parameter.filter === 'select'"
        :parameter="parameter"
        :group-name="groupName"
        :is-detail="isDetail"
        @changed="handleSelectChanged"
      />
      <div v-for="item in parameter.filter === 'slider' || parameter.filter === 'select' ? [] : parameter.values" :key="item.id">
        <FilterParameterValue :parameter="parameter" :item="item" :group-name="groupName" :is-detail="isDetail" @changed="(value) => handleChanged(item, value)" />
      </div>
    </template>
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { useId } from 'vue';
import { parseParameterQuery } from '@utils/parameters';
import PlusIcon from "@components/icons/plus-icon";
import MinusIcon from "@components/icons/minus-icon";
import FilterParameterValue from "@components/filter/filter-parameter-value";

const props = defineProps({
  parameter: {
    type: Object,
    required: true
  },
  queryCode:  {
    type: String,
    default: 'p'
  },
  isDetail: {
    type: Boolean,
    default: false
  }
});

const route = useRoute();
const queryKey = `${props.queryCode}[${props.parameter.idParameter}]`;
const groupName = `${queryKey}_${useId()}`;
const rangeLimits = { min: 0, max: 100 };
const range = reactive({ valueMin: rangeLimits.min, valueMax: rangeLimits.max });
const rangeTrackStyle = computed(() => ({
  left: `${(range.valueMin - rangeLimits.min) / (rangeLimits.max - rangeLimits.min) * 100}%`,
  right: `${(rangeLimits.max - range.valueMax) / (rangeLimits.max - rangeLimits.min) * 100}%`,
}));

watch([() => route.query[queryKey], () => props.parameter], ([value]) => {
  const parsed = parseParameterQuery(value, props.parameter);
  range.valueMin = Math.max(rangeLimits.min, Math.min(rangeLimits.max, parsed?.valueMin ?? rangeLimits.min));
  range.valueMax = Math.max(range.valueMin, Math.min(rangeLimits.max, parsed?.valueMax ?? rangeLimits.max));
  for (const item of props.parameter.values) item.value = parsed?.idValues?.includes(item.id) ?? false;
}, { immediate: true });

const clampRange = bound => {
  if (bound === 'valueMin') range.valueMin = Math.min(range.valueMin, range.valueMax);
  else range.valueMax = Math.max(range.valueMin, range.valueMax);
};

const handleRangeChanged = async () => {
  const valueMin = range.valueMin === rangeLimits.min ? null : range.valueMin;
  const valueMax = range.valueMax === rangeLimits.max ? null : range.valueMax;
  const queryValue = valueMin === null && valueMax === null ? '' : `${valueMin ?? ''}..${valueMax ?? ''}`;
  await useSearchParam(queryKey, queryValue);
};

const isOpen = ref(_.includes(_.keys(route.query), `${props.queryCode}[${props.parameter.idParameter}]`));

const toggleCollapse = () => {
  isOpen.value = !isOpen.value;
}

const handleChanged = async (parameterValue, value) => {
  if (props.parameter.filter === 'radio') {
    for (const item of props.parameter.values) item.value = value && item.id === parameterValue.id;
    await useSearchParam(queryKey, value ? String(parameterValue.id) : '');
    return;
  }
  parameterValue.value = value;
  const checked = _.chain(props.parameter.values)
    .filter((item) => parameterValue.id === item.id ? value : item.value)
    .map((item) => item.id)
    .join(',')
    .value();
  await useSearchParam(queryKey, checked);
}

const handleSelectChanged = async (selectedIds) => {
  for (const item of props.parameter.values) item.value = selectedIds.includes(item.id);
  await useSearchParam(queryKey, selectedIds.join(','));
}
</script>

<style scoped>
.parameter-range-input {
  position: absolute;
  top: 0;
  left: -0.75rem;
  width: calc(100% + 1.5rem);
  height: 2rem;
  margin: 0;
  padding: 0;
  appearance: none;
  color: inherit;
  background: transparent;
  pointer-events: none;
}

.parameter-range-input::-webkit-slider-runnable-track {
  height: 0.25rem;
  background: transparent;
}

.parameter-range-input::-moz-range-track {
  height: 0.25rem;
  background: transparent;
}

.parameter-range-input::-webkit-slider-thumb {
  width: 1.5rem;
  height: 1.5rem;
  margin-top: -0.625rem;
  appearance: none;
  border: 2px solid white;
  border-radius: 50%;
  background: currentcolor;
  box-shadow: 0 0 0 1px currentcolor;
  cursor: grab;
  pointer-events: auto;
}

.parameter-range-input::-moz-range-thumb {
  box-sizing: border-box;
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid white;
  border-radius: 50%;
  background: currentcolor;
  box-shadow: 0 0 0 1px currentcolor;
  cursor: grab;
  pointer-events: auto;
}

.parameter-range-input:focus-visible {
  outline: none;
}

.parameter-range-input:focus-visible::-webkit-slider-thumb {
  outline: 2px solid currentcolor;
  outline-offset: 3px;
}

.parameter-range-input:focus-visible::-moz-range-thumb {
  outline: 2px solid currentcolor;
  outline-offset: 3px;
}
</style>
